
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Trash2, Save, Search } from "lucide-react";
import { Medication } from "@/types";
import { formatMedicationDuration } from '@/lib/utils/dateUtils';
import { useState, useEffect } from "react";
import { SaveTemplateDialog } from "./SaveTemplateDialog";
import { TemplateSearchDialog } from "./TemplateSearchDialog";

interface MedicationTableProps {
  medications: Medication[];
  onDelete: (index: number) => void;
  onLoadTemplate?: (medications: Medication[]) => void;
  onEditMedication?: (medication: Medication, index: number) => void;
}

export function MedicationTable({ medications, onDelete, onLoadTemplate, onEditMedication }: MedicationTableProps) {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showSearchDialog, setShowSearchDialog] = useState(false);






  const handleLoadTemplate = (templateMedications: Medication[]) => {
    if (onLoadTemplate) {
      onLoadTemplate(templateMedications);
    }
  };

  const handleDoubleClick = (medication: Medication, index: number) => {
    if (onEditMedication) {
      onEditMedication(medication, index);
    }
  };

  return (
    <div className="space-y-4">
      {medications.length === 0 ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">No medications added yet.</p>
        </div>
      ) : (
        <div className="relative">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Medication</TableHead>
                <TableHead>Dosage</TableHead>
                <TableHead>Frequency</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Instructions</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {medications.map((medication, index) => (
                  <TableRow 
                    key={index}
                    className="cursor-pointer hover:bg-muted/50"
                    onDoubleClick={() => handleDoubleClick(medication, index)}
                    title="Double-click to edit medication"
                  >
                    <TableCell className="font-medium">{medication.name}</TableCell>
                    <TableCell>{medication.dosage} {medication.doseType ? medication.doseType : ''}</TableCell>
            <TableCell>
              <div className="font-medium">{medication.frequency || 'No frequency'}</div>
            </TableCell>
                    <TableCell>{formatMedicationDuration(medication.duration)}</TableCell>
                    <TableCell className="max-w-[200px] truncate" title={medication.instructions}>
                      {medication.instructions}
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => onDelete(index)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Template Action Buttons */}
      <div className="flex justify-end space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowSearchDialog(true)}
          className="h-8"
        >
          <Search className="h-4 w-4 mr-1" />
          Load Template
        </Button>
        {medications.length > 0 && (
          <Button
            size="sm"
            onClick={() => setShowSaveDialog(true)}
            className="h-8"
          >
            <Save className="h-4 w-4 mr-1" />
            Save as Template
          </Button>
        )}
      </div>

      {/* Dialogs */}
      <SaveTemplateDialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        medications={medications}
      />

      <TemplateSearchDialog
        isOpen={showSearchDialog}
        onClose={() => setShowSearchDialog(false)}
        onLoadTemplate={handleLoadTemplate}
      />
    </div>
  );
}
